import { createContext, useContext, useEffect, useState } from "react";
import api, { isNetworkError } from "./api";

const AuthContext = createContext(null);

export const ROLES = {
    ADMIN: "admin",
    MANAGER: "manager",
    ASSOCIATE: "associate",
    PRODUCTION: "production",
    QUOTE: "quote",
    QUOTE_USER: "quote_user",
    SALES_QUOTE: "sales_quote",
};

export const ROLE_LABELS = {
    admin: "Admin",
    manager: "Manager",
    associate: "Associate",
    production: "Production",
    quote: "Quote User",
    quote_user: "Quote User",
    sales_quote: "Sales Quote",
};

export const ADMIN_ROLES = [ROLES.ADMIN];
export const MANAGER_ROLES = [ROLES.ADMIN, ROLES.MANAGER];
export const QUOTE_WORKFLOW_ROLES = [ROLES.ADMIN, ROLES.MANAGER, ROLES.ASSOCIATE, ROLES.PRODUCTION, ROLES.QUOTE, ROLES.QUOTE_USER, ROLES.SALES_QUOTE];
export const EMPLOYEE_ROLES = QUOTE_WORKFLOW_ROLES;
export const QUICKBOOKS_ROLES = [ROLES.ADMIN];

export function userHasRole(user, roles = []) {
    if (!user?.role) return false;
    return roles.includes(user.role);
}

export function isAdmin(user) {
    return userHasRole(user, ADMIN_ROLES);
}

export function getAuthenticatedLandingPath(user) {
    return "/dashboard";
}

export function isManagerOrAdmin(user) {
    return userHasRole(user, MANAGER_ROLES);
}

export function canAccessQuickBooks(user) {
    return userHasRole(user, QUICKBOOKS_ROLES);
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        try {
            const u = localStorage.getItem("ehs_user");
            return u ? JSON.parse(u) : null;
        } catch {
            return null;
        }
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const embedded = window.self !== window.top;
        const params = new URLSearchParams(window.location.search);
        api.get("/auth/me")
            .then((r) => {
                setUser(r.data);
                localStorage.setItem("ehs_user", JSON.stringify(r.data));
            })
            .catch(() => {
                localStorage.removeItem("ehs_token");
                localStorage.removeItem("ehs_user");
                setUser(null);
                if (embedded && params.get("code")) {
                    window.location.assign(`${api.defaults.baseURL}/auth/ghl/embedded-login?code=${encodeURIComponent(params.get("code"))}`);
                }
            })
            .finally(() => setLoading(false));
    }, []);

    const login = async (email, password) => {
        try {
            const res = await api.post("/auth/login", { email, password });
            localStorage.setItem("ehs_token", res.data.access_token);
            localStorage.setItem("ehs_user", JSON.stringify(res.data.user));
            setUser(res.data.user);
            return res.data.user;
        } catch (err) {
            if (isNetworkError(err)) {
                err.userMessage = "Unable to reach the EHS server. Please try again or contact an administrator.";
            } else if (err.response?.status === 401) {
                err.userMessage = "Invalid email or password.";
            }
            throw err;
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            // Local state still needs to be cleared if the server is unavailable.
        }
        localStorage.removeItem("ehs_token");
        localStorage.removeItem("ehs_user");
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
